import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { IconArrowLeft, IconArrowUpRight, IconChevronDown, IconFileDescription, IconSearch, IconX } from '@tabler/icons-react';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import { AnimatedPresence } from '../ui/AnimatedPresence';
import { ButtonContent } from '../ui/ButtonContent';
import { CollectionPresence } from '../ui/CollectionPresence';
import { Skeleton } from '../ui/Skeleton';
import { searchNestedFormSources, loadNestedFormSource, type NestedFormSelection, type NestedFormSummary } from './nested-form-sources';

interface Results {
  term: string;
  rows: NestedFormSummary[];
  offset: number;
  nextOffset: number;
  hasMore: boolean;
}

/** The second page of the add-block menu. Focus stays in search while the row highlight moves. */
export function NestedFormPicker({ orgId, workspaceId, campaignId, onBack, onSelect, signal }: NestedFormSelection & {
  onBack: () => void; signal: AbortSignal;
}) {
  const listId = useId();
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const selecting = useRef(false);
  const nextHighlight = useRef<number | null>(null);
  const [query, setQuery] = useState('');
  const term = query.trim();
  const [results, setResults] = useState<Results>();
  const [offset, setOffset] = useState(0);
  const [retry, setRetry] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [busyId, setBusyId] = useState('');
  const [searchError, setSearchError] = useState(false);
  const [selectionError, setSelectionError] = useState('');
  const [fetching, setFetching] = useState(true);
  const current = results?.term === term ? results : undefined;
  const active = current?.rows[activeIndex];

  useLayoutEffect(() => { input.current?.focus({ preventScroll: true }); }, []);
  useEffect(() => {
    const controller = new AbortController();
    const abort = () => controller.abort();
    if (signal.aborted) return undefined;
    signal.addEventListener('abort', abort, { once: true });
    setFetching(true);
    setSearchError(false);
    const timer = setTimeout(async () => {
      try {
        const { rows, hasMore } = await searchNestedFormSources({ orgId, workspaceId, campaignId, search: term, offset, signal: controller.signal });
        if (controller.signal.aborted) return;
        setResults(previous => {
          const prior = offset && previous?.term === term ? previous.rows : [];
          const seen = new Set(prior.map(row => row.id));
          return { term, offset, rows: [...prior, ...rows.filter(row => !seen.has(row.id))], hasMore, nextOffset: offset + rows.length };
        });
      } catch (cause) {
        if (controller.signal.aborted) return;
        captureException(cause as Error, 'NestedFormPicker.search', { workspaceId });
        setSearchError(true);
      } finally {
        if (!controller.signal.aborted) setFetching(false);
      }
    }, term && !offset ? 140 : 0);
    return () => { clearTimeout(timer); abort(); signal.removeEventListener('abort', abort); };
  }, [orgId, workspaceId, campaignId, term, offset, retry, signal]);

  // Reveal the first result of the new page even when the More button disappears.
  useLayoutEffect(() => {
    if (!current || current.offset !== offset || nextHighlight.current === null) return;
    const index = Math.max(0, Math.min(nextHighlight.current, current.rows.length - 1));
    nextHighlight.current = null;
    setActiveIndex(index);
    input.current?.focus({ preventScroll: true });
    list.current?.querySelectorAll('[role="option"]')[index]?.scrollIntoView?.({ block: 'nearest' });
  }, [current, offset]);

  const changeQuery = (value: string) => {
    nextHighlight.current = null;
    setQuery(value); setOffset(0); setActiveIndex(0); setSelectionError(''); setSearchError(false);
    if (list.current) list.current.scrollTop = 0;
  };

  const choose = async (id: string) => {
    if (selecting.current || signal.aborted) return;
    selecting.current = true;
    setBusyId(id); setSelectionError('');
    try {
      const source = await loadNestedFormSource(orgId, workspaceId, id, signal);
      if (signal.aborted) return;
      try { onSelect(source); }
      catch (cause) { setSelectionError(cause instanceof Error ? cause.message : 'Choose another form.'); }
    } catch (cause) {
      if (signal.aborted) return;
      captureException(cause as Error, 'NestedFormPicker.load', { workspaceId, sourceId: id });
      setSelectionError('Couldn’t open this form. Choose it again to retry.');
    } finally {
      selecting.current = false;
      if (!signal.aborted) setBusyId('');
    }
  };

  const navigate = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing || busyId) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      if (active) void choose(active.id);
    } else if (current?.rows.length && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault();
      const index = Math.min(current.rows.length - 1, Math.max(0, activeIndex + (event.key === 'ArrowDown' ? 1 : -1)));
      setActiveIndex(index);
      list.current?.querySelectorAll('[role="option"]')[index]?.scrollIntoView?.({ block: 'nearest' });
    }
  };

  const mode = current?.rows.length ? 'rows' : searchError ? 'error' : current ? 'empty' : 'loading';
  return <>
    <div className="flex shrink-0 items-center gap-2 px-3 pb-1 pt-2">
      <button type="button" aria-label="Back to blocks" title="Back to blocks" onClick={onBack}
        className="icon-button -ml-1"><IconArrowLeft size={16} aria-hidden /></button>
      <span className="text-sm font-semibold text-foreground">Nested form</span>
    </div>
    <div className="mx-3 mb-2 flex shrink-0 items-center gap-2 rounded-lg border border-border bg-background px-2.5 focus-within:border-volt-soft">
      <IconSearch size={16} aria-hidden className="shrink-0 text-muted-foreground" />
      <input ref={input} role="combobox" aria-label="Search forms" aria-expanded="true" aria-autocomplete="list"
        aria-controls={listId} aria-activedescendant={active ? `${listId}-${active.id}` : undefined}
        value={query} readOnly={!!busyId} onChange={event => changeQuery(event.target.value)} onKeyDown={navigate}
        autoComplete="off" placeholder="Search forms" className="h-10 min-w-0 flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none" />
      <button type="button" aria-label="Clear search" aria-hidden={!query} tabIndex={query ? 0 : -1}
        disabled={!query || !!busyId} onClick={() => { changeQuery(''); input.current?.focus(); }}
        className={`grid h-7 w-7 place-items-center rounded-md text-muted-foreground transition-opacity hover:bg-surface-hover motion-reduce:transition-none ${query ? 'opacity-100' : 'pointer-events-none opacity-0'}`}><IconX size={14} aria-hidden /></button>
    </div>
    <div ref={list} className="min-h-0 flex-1 overflow-y-auto overscroll-contain border-t border-border">
      <CollectionPresence contentKey={`${mode}-${current?.term ?? ''}`}>
        <div className="px-3 pb-1 pt-2.5 text-[11px] font-medium text-muted-foreground">{term ? 'Search results' : 'Recent forms'}</div>
        <div id={listId} role="listbox" aria-label="Workspace forms" aria-busy={fetching || !!busyId} className="px-1.5 pb-1.5">
          {current?.rows.map((row, index) => <div key={row.id} id={`${listId}-${row.id}`} role="option" aria-selected={activeIndex === index}
            aria-label={`${row.target_name}, ${row.question_count} ${row.question_count === 1 ? 'question' : 'questions'}${row.room_id ? `, ${row.room_id}` : ''}`}
            aria-disabled={!!busyId} onPointerMove={() => { if (!busyId) setActiveIndex(index); }}
            onMouseDown={event => event.preventDefault()} onClick={() => void choose(row.id)} title={row.target_name}
            className={`flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-2 transition-colors motion-reduce:transition-none ${activeIndex === index ? 'bg-surface-hover' : ''}`}>
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-border bg-surface-raised text-muted-foreground"><IconFileDescription size={16} stroke={1.6} aria-hidden /></span>
            <span className="min-w-0 flex-1"><span className="line-clamp-2 break-words text-sm font-medium leading-5 text-foreground">{row.target_name}</span>
              <span className="block truncate text-[11px] text-muted-foreground">{row.question_count} {row.question_count === 1 ? 'question' : 'questions'}{row.room_id && <> · <span className="font-mono">{row.room_id}</span></>}</span>
            </span>
            <span aria-hidden className={`shrink-0 text-muted-foreground transition-opacity motion-reduce:transition-none ${activeIndex === index || busyId === row.id ? 'opacity-100' : 'opacity-0'}`}>
              <ButtonContent loading={busyId === row.id}><IconArrowUpRight size={15} /></ButtonContent>
            </span>
          </div>)}
        </div>
        {mode === 'loading' && <div aria-hidden className="space-y-3 px-3 pb-3">{[62,76,49].map(width => <div key={width} className="flex items-center gap-2.5"><Skeleton width={32} height={32} borderRadius={8} /><span className="flex-1"><Skeleton width={`${width}%`} height={12} /><br/><Skeleton width="34%" height={9} /></span></div>)}</div>}
        {mode === 'empty' && <p role="status" className="px-3 pb-6 pt-2 text-sm text-foreground">{term ? 'No matching forms' : 'No forms available in this workspace'}</p>}
      </CollectionPresence>
    </div>
    <AnimatedPresence show={!!selectionError || searchError} className="shrink-0"><div role="alert" className="border-t border-border px-3 py-2 text-xs text-foreground">
      {selectionError || 'Couldn’t search forms.'} {searchError && <button type="button" className="font-semibold underline" onClick={() => setRetry(value => value + 1)}>Retry</button>}
    </div></AnimatedPresence>
    {current?.hasMore && !searchError && <button type="button" aria-label="More forms" disabled={fetching || !!busyId} aria-busy={fetching}
      onClick={() => { nextHighlight.current = current.rows.length; setOffset(current.nextOffset); }} className="flex shrink-0 items-center justify-center border-t border-border px-3 py-2 text-xs text-muted-foreground hover:bg-surface-hover hover:text-foreground">
      <ButtonContent loading={fetching}>More forms <IconChevronDown size={13} aria-hidden /></ButtonContent>
    </button>}
  </>;
}
